import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, API, eur, up, catLabel, gr, addYear, reportUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, MagicWand, MapPin, CurrencyEur, FilePdf, QrCode, Paperclip, MagnifyingGlass, Receipt } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import BoatDetailsDialog from "@/components/BoatDetailsDialog";
import BoatDocumentsDialog from "@/components/BoatDocumentsDialog";
import { DateField } from "@/components/DateField";

const empty = { name: "", owner: "", owner_id_type: "afm", owner_id_number: "", registry_no: "", customer_email: "", customer_phone: "", length: "", period_id: "", slot_id: "", start_date: "", end_date: "", billing_mode: "monthly", units: 1, special_price: "", previous_balance: "", beam: "", draft: "" };

export default function Boats() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [docsBoat, setDocsBoat] = useState(null);
  const [search, setSearch] = useState("");
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [fiscal, setFiscal] = useState(null);
  const openDoc = (b) => setFiscal({ boat: b, doc_type: "receipt", amount: String(b.balance_to_end ?? b.amount ?? ""), doc_date: new Date().toISOString().slice(0, 10), note: "" });
  const issueDoc = async () => {
    try {
      const { data } = await api.post("/fiscal/issue", {
        boat_id: fiscal.boat.id,
        doc_type: fiscal.doc_type,
        amount: Number(fiscal.amount || 0),
        doc_date: fiscal.doc_date || null,
        note: fiscal.note || null,
        period_end: fiscal.boat.end_date || null,
      });
      window.open(reportUrl(`/fiscal/${data.id}.pdf`), "_blank");
      setFiscal(null);
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["fiscal"] });
      qc.invalidateQueries({ queryKey: ["fiscal-pending"] });
      if (data.mydata_status === "success") toast.success(`Στάλθηκε στο myDATA · ΜΑΡΚ ${data.mydata_mark}`);
      else if (data.mydata_status === "error") toast.warning(`Εκδόθηκε, αλλά η αποστολή στο myDATA απέτυχε: ${data.mydata_error || ""}`);
      else toast.success(`Εκδόθηκε ${data.series}/${data.aa}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα έκδοσης παραστατικού");
    }
  };

  const { data: config } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const { data: boats = [], isLoading } = useQuery({ queryKey: ["boats"], queryFn: async () => (await api.get("/boats")).data });
  const { data: slots = [] } = useQuery({ queryKey: ["slots"], queryFn: async () => (await api.get("/slots")).data });

  const q = search.trim().toLowerCase();
  const filteredBoats = q
    ? boats.filter((b) =>
        [b.name, b.owner, b.registry_no, b.slot_number, b.customer_phone]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    : boats;

  const openNew = () => {
    setForm({ ...empty, period_id: config?.periods?.[0]?.id || "" });
    setEditId(null);
    setSuggestion(null);
    setOpen(true);
  };
  const openEdit = (b) => {
    setForm({ name: b.name, owner: b.owner, owner_id_type: b.owner_id_type || "afm", owner_id_number: b.owner_id_number || "", registry_no: b.registry_no || "", customer_email: b.customer_email || "", customer_phone: b.customer_phone || "", length: String(b.length), period_id: b.period_id, slot_id: b.slot_id, start_date: b.start_date || "", end_date: b.end_date || "", billing_mode: b.billing_mode || "monthly", units: b.units || 1, special_price: b.special_price != null ? String(b.special_price) : "", previous_balance: b.previous_balance != null ? String(b.previous_balance) : "", beam: b.beam != null ? String(b.beam) : "", draft: b.draft != null ? String(b.draft) : "" });
    setEditId(b.id);
    setSuggestion(null);
    setOpen(true);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && config && (!searchParams.get("slot") || slots.length)) {
      const slotNum = searchParams.get("slot");
      const slot = slotNum ? slots.find((s) => String(s.number) === String(slotNum)) : null;
      setForm({
        ...empty,
        period_id: config?.periods?.[0]?.id || "",
        length: searchParams.get("length") || "",
        start_date: searchParams.get("start") || "",
        end_date: searchParams.get("end") || "",
        slot_id: slot ? slot.id : "",
      });
      setEditId(null);
      setSuggestion(null);
      setOpen(true);
      ["new", "slot", "length", "start", "end"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
    const editId = searchParams.get("edit");
    if (editId && boats.length) {
      const b = boats.find((x) => x.id === editId);
      if (b) openEdit(b);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    const focusId = searchParams.get("focus");
    if (focusId && boats.length) {
      setDetailsId(focusId);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="boat-row-${focusId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-inset", "ring-[#0055FF]", "bg-[#EFF6FF]");
          setTimeout(() => el.classList.remove("ring-2", "ring-inset", "ring-[#0055FF]", "bg-[#EFF6FF]"), 2500);
        }
      }, 300);
      searchParams.delete("focus");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, config, boats, slots]);

  const fetchSuggestion = async (len) => {
    if (!len || Number(len) <= 0) return setSuggestion(null);
    try {
      const res = (await api.get("/suggest-slot", { params: { length: Number(len) } })).data;
      setSuggestion(res);
      if (!editId && res.slot) setForm((f) => ({ ...f, slot_id: "" }));
    } catch (e) {
      setSuggestion({ error: e.response?.data?.detail || "Σφάλμα" });
    }
  };

  const availableSlots = slots.filter((s) => {
    const fits = s.max_length >= Number(form.length || 0);
    const free = !s.occupied || s.id === (editId ? form.slot_id : null);
    return fits && free;
  });

  const ownerOptions = Object.values(
    boats.reduce((acc, b) => {
      const key = (b.owner || "").trim();
      if (key && !acc[key]) acc[key] = { owner: key, customer_email: b.customer_email, customer_phone: b.customer_phone, owner_id_type: b.owner_id_type, owner_id_number: b.owner_id_number };
      return acc;
    }, {})
  ).sort((a, b) => a.owner.localeCompare(b.owner, "el"));

  const pickOwner = (val) => {
    const match = ownerOptions.find((o) => o.owner === val);
    setForm((f) => ({
      ...f,
      owner: val,
      ...(match
        ? {
            customer_email: match.customer_email || f.customer_email,
            customer_phone: match.customer_phone || f.customer_phone,
            owner_id_type: match.owner_id_type || f.owner_id_type,
            owner_id_number: match.owner_id_number || f.owner_id_number,
          }
        : {}),
    }));
  };

  const save = async () => {    if (!form.name.trim() || !form.owner.trim() || !form.length) {
      return toast.error("Συμπληρώστε όνομα σκάφους, ιδιοκτήτη και μήκος");
    }
    if (!form.owner_id_number.trim()) {
      return toast.error("Συμπληρώστε Α.Φ.Μ. / Α.Δ.Τ. ή αριθμό διαβατηρίου του ιδιοκτήτη");
    }
    if (!form.start_date) {
      return toast.error("Δηλώστε ημερομηνία εισόδου");
    }
    if (form.end_date && form.end_date < form.start_date) {
      return toast.error("Η έξοδος δεν μπορεί να είναι πριν την είσοδο");
    }
    const payload = {
      name: form.name.trim(),
      owner: form.owner.trim(),
      registry_no: form.registry_no || null,
      customer_email: form.customer_email || null,
      customer_phone: form.customer_phone || null,
      length: Number(form.length),
      slot_id: form.slot_id || null,
      start_date: form.start_date,
      end_date: form.end_date,
      billing_mode: form.billing_mode,
      special_price: form.special_price === "" ? null : Number(form.special_price),
      owner_id_type: form.owner_id_type,
      owner_id_number: form.owner_id_number.trim(),
      previous_balance: form.previous_balance === "" ? null : Number(form.previous_balance),
      beam: form.beam === "" ? null : Number(form.beam),
      draft: form.draft === "" ? null : Number(form.draft),
    };
    try {
      if (editId) await api.put(`/boats/${editId}`, payload);
      else await api.post("/boats", payload);
      await qc.invalidateQueries();
      toast.success(editId ? "Ενημερώθηκε" : "Καταχωρήθηκε το σκάφος");
      setOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail || "Σφάλμα";
      toast.error(detail);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/boats/${deleteId}`);
      await qc.invalidateQueries();
      toast.success("Διαγράφηκε");
    } catch (e) {
      toast.error("Σφάλμα διαγραφής");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Σκάφη"
        subtitle={`${boats.length} καταχωρημένα σκάφη`}
        action={
          <Button onClick={openNew} data-testid="add-boat-btn" className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]">
            <Plus size={18} weight="bold" /> Νέο σκάφος
          </Button>
        }
      />
      <div className="p-6">
        <div className="mb-4 max-w-md">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Αναζήτηση σκάφους (όνομα, ιδιοκτήτης, νηολόγιο, θέση)…"
            className="rounded-sm"
            data-testid="boats-search-input"
          />
        </div>
        <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="boats-table">
              <thead>
                <tr className="border-b border-border text-left text-xs tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Σκάφος</th>
                  <th className="px-4 py-3 font-semibold">Ιδιοκτήτης</th>
                  <th className="px-4 py-3 font-semibold">Μήκος</th>
                  <th className="px-4 py-3 font-semibold">Θέση</th>
                  <th className="px-4 py-3 font-semibold">Αρχικός χρόνος εισόδου</th>
                  <th className="px-4 py-3 font-semibold">Αναμενόμενος χρόνος εξόδου</th>
                  <th className="px-4 py-3 text-right font-semibold">Χρέωση έως σήμερα</th>
                  <th className="px-4 py-3 text-right font-semibold">Υπόλοιπο στη λήξη</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Φόρτωση...</td></tr>
                ) : boats.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Δεν υπάρχουν σκάφη. Πατήστε «Νέο σκάφος».</td></tr>
                ) : filteredBoats.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Δεν βρέθηκαν σκάφη για «{search}».</td></tr>
                ) : (
                  filteredBoats.map((b) => (
                    <tr key={b.id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`boat-row-${b.id}`}>
                      <td className="px-4 py-3 font-medium">
                        <div>{b.name}</div>
                        {b.registry_no && <div className="text-[11px] font-normal text-muted-foreground">{b.registry_no}</div>}
                      </td>
                      <td className="px-4 py-3">{b.owner}</td>
                      <td className="px-4 py-3 tabular-nums">{b.length} m</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 rounded-sm bg-[#234E7D] px-2 py-0.5 text-xs font-semibold text-white">
                          <MapPin size={12} weight="bold" /> {b.slot_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold tabular-nums">{gr(b.original_start_date || b.start_date) || "—"}</div>
                        {b.original_start_date && b.original_start_date !== b.start_date && (
                          <div className="text-[11px] text-muted-foreground">τρέχουσα περίοδος: {gr(b.start_date)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs tabular-nums">{gr(b.end_date) || "—"}</div>
                        {b.days_left != null && (
                          <span className={`text-[11px] font-semibold ${b.days_left < 0 ? "text-destructive" : b.days_left <= 7 ? "text-[#B45309]" : "text-muted-foreground"}`}>
                            {b.days_left < 0 ? `έληξε ${Math.abs(b.days_left)}ημ.` : `σε ${b.days_left}ημ.`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                    <div className="font-semibold tabular-nums text-foreground">{eur(b.charged_to_date)}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                        <div className="font-semibold tabular-nums text-foreground">{eur(b.balance_to_end)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-black" onClick={() => setDetailsId(b.id)} data-testid={`details-boat-${b.id}`}>
                            <CurrencyEur size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-[#B91C1C]" onClick={() => window.open(reportUrl(`/reports/boat/${b.id}.pdf`), "_blank")} data-testid={`pdf-boat-${b.id}`}>
                            <FilePdf size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-[#0F766E]" onClick={() => openDoc(b)} data-testid={`fiscal-boat-${b.id}`} title="Έκδοση τιμολογίου / απόδειξης">
                            <Receipt size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(b)} data-testid={`edit-boat-${b.id}`}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-[#0055FF]" onClick={() => { const token = localStorage.getItem("token"); window.open(`${API}/boats/${b.id}/qr.pdf?base=${encodeURIComponent(window.location.origin)}&token=${token}`, "_blank"); }} data-testid={`qr-boat-${b.id}`}>
                            <QrCode size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-[#234E7D]" onClick={() => setDocsBoat(b)} data-testid={`docs-boat-${b.id}`}>
                            <Paperclip size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-destructive" onClick={() => setDeleteId(b.id)} data-testid={`delete-boat-${b.id}`}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="boat-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Επεξεργασία στοιχείων σκάφους" : "Νέο σκάφος"}</DialogTitle>
            <DialogDescription>Συμπληρώστε τα στοιχεία του σκάφους. Η θέση προτείνεται αυτόματα βάσει μήκους.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Όνομα σκάφους</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: up(e.target.value) })} className="mt-1 rounded-sm" data-testid="boat-name-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Ιδιοκτήτης</Label>
                <div className="mt-1 flex items-center gap-1">
                  <Input
                    value={form.owner}
                    onChange={(e) => setForm({ ...form, owner: up(e.target.value) })}
                    className="rounded-sm"
                    placeholder="νέος ιδιοκτήτης"
                    data-testid="boat-owner-input"
                  />
                  <Popover open={ownerPickerOpen} onOpenChange={setOwnerPickerOpen} modal>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-sm" data-testid="boat-owner-picker" aria-label="Λίστα πελατών">
                        <MagnifyingGlass size={16} weight="bold" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" side="bottom" sideOffset={4} className="w-72 p-0">
                      <div className="border-b border-border p-2">
                        <Input
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                          placeholder="Αναζήτηση πελάτη..."
                          className="rounded-sm"
                          data-testid="boat-owner-search"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {ownerOptions.filter((o) => o.owner.toLowerCase().includes(ownerSearch.toLowerCase())).length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-muted-foreground">Κανένας πελάτης</div>
                        ) : (
                          ownerOptions
                            .filter((o) => o.owner.toLowerCase().includes(ownerSearch.toLowerCase()))
                            .map((o) => (
                              <button
                                key={o.owner}
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                                onClick={() => { pickOwner(o.owner); setOwnerPickerOpen(false); setOwnerSearch(""); }}
                                data-testid={`owner-option-${o.owner}`}
                              >
                                <div className="font-medium">{o.owner}</div>
                                {(o.owner_id_number || o.customer_phone) && (
                                  <div className="text-[11px] text-muted-foreground">{[o.owner_id_number, o.customer_phone].filter(Boolean).join(" · ")}</div>
                                )}
                              </button>
                            ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Τύπος στοιχείου *</Label>
                <Select value={form.owner_id_type} onValueChange={(v) => setForm({ ...form, owner_id_type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="boat-owner-id-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="afm">Α.Φ.Μ.</SelectItem>
                    <SelectItem value="adt">Αριθμός δελτίου ταυτότητας</SelectItem>
                    <SelectItem value="passport">Αριθμός διαβατηρίου</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Αριθμός στοιχείου ταυτοποίησης *</Label>
                <Input value={form.owner_id_number} onChange={(e) => setForm({ ...form, owner_id_number: up(e.target.value) })} className="mt-1 rounded-sm" placeholder="υποχρεωτικό — αναφέρεται στο συμφωνητικό" data-testid="boat-owner-id-number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Email πελάτη</Label>
                <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1 rounded-sm" data-testid="boat-email-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Τηλέφωνο πελάτη</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1 rounded-sm" data-testid="boat-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Αριθμός νηολογίου</Label>
                <Input value={form.registry_no} onChange={(e) => setForm({ ...form, registry_no: up(e.target.value) })} className="mt-1 rounded-sm" data-testid="boat-registry-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Μήκος (m)</Label>
                <Input type="number" value={form.length} onChange={(e) => { setForm({ ...form, length: e.target.value }); fetchSuggestion(e.target.value); }} className="mt-1 rounded-sm" data-testid="boat-length-input" />
              </div>
              {config?.facility_type === "sea" && (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">Πλάτος (m)</Label>
                    <Input type="number" step="0.1" value={form.beam} onChange={(e) => setForm({ ...form, beam: e.target.value })} className="mt-1 rounded-sm" data-testid="boat-beam-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Βύθισμα (m)</Label>
                    <Input type="number" step="0.1" value={form.draft} onChange={(e) => setForm({ ...form, draft: e.target.value })} className="mt-1 rounded-sm" data-testid="boat-draft-input" />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Τρόπος χρέωσης</Label>
                <Select value={form.billing_mode} onValueChange={(v) => setForm({ ...form, billing_mode: v, end_date: v === "yearly" && form.start_date ? addYear(form.start_date) : form.end_date })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="boat-billing-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Μηνιαία</SelectItem>
                    <SelectItem value="yearly">Ετήσια</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Ημερομηνία εισόδου</Label>
                <DateField value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v, end_date: form.billing_mode === "yearly" && v ? addYear(v) : form.end_date })} testId="boat-start-date" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Προβλεπόμενη ημερομηνία εξόδου</Label>
                <DateField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="boat-end-date" disabled={form.billing_mode === "yearly"} />
              </div>
            </div>

            <div className="rounded-sm border border-border bg-secondary/40 p-3">
              <Label className="text-xs font-semibold text-muted-foreground">Χρέωση κατόπιν ειδικής συμφωνίας (€ / έτος)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.special_price}
                onChange={(e) => setForm({ ...form, special_price: e.target.value })}
                placeholder="π.χ. 1500"
                className="mt-1 rounded-sm"
                data-testid="boat-special-price-input"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Αν συμπληρωθεί, ισχύει αυτή η χρέωση και αγνοείται η τιμή κατηγορίας / ανά μέτρο. Διατηρείται στις ανανεώσεις.
              </p>
              <div className="mt-3">
                <Label className="text-xs font-semibold text-muted-foreground">Υπόλοιπο οφειλής πριν την τελευταία είσοδο (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.previous_balance}
                  onChange={(e) => setForm({ ...form, previous_balance: e.target.value })}
                  placeholder="π.χ. 350"
                  className="mt-1 rounded-sm"
                  data-testid="boat-previous-balance-input"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Προηγούμενες οφειλές του πελάτη. Προστίθενται στο σημερινό υπόλοιπο.
                </p>
              </div>
            </div>

            {suggestion && !suggestion.error && (
              <div className="rounded-sm border border-[#0055FF]/30 bg-[#EFF6FF] p-3 text-sm" data-testid="slot-suggestion">
                <div className="flex items-center gap-2 font-medium text-[#0044CC]">
                  <MagicWand size={16} weight="bold" />
                  {suggestion.slot
                    ? `Προτεινόμενη θέση: #${suggestion.slot.number} (${suggestion.category?.name})`
                    : `Καμία διαθέσιμη θέση για ${suggestion.category?.name}`}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Κατηγορία βάσει μήκους: {suggestion.category?.name}</p>
              </div>
            )}
            {suggestion?.error && (
              <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="slot-error">{suggestion.error}</div>
            )}

            <div>
              <Label className="text-xs text-muted-foreground">Θέση (κενό = αυτόματη ανάθεση)</Label>
              <Select value={form.slot_id || "auto"} onValueChange={(v) => setForm({ ...form, slot_id: v === "auto" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="boat-slot-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Αυτόματη ανάθεση (προτεινόμενη)</SelectItem>
                  {availableSlots.map((s) => (
                    <SelectItem key={s.id} value={s.id}>#{s.number} — {catLabel(s.category_name)} (έως {s.max_length}m)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={save} data-testid="boat-save-btn">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Διαγραφή σκάφους;</AlertDialogTitle>
            <AlertDialogDescription>Η θέση θα ελευθερωθεί. Η ενέργεια δεν αναιρείται.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Άκυρο</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-btn">Διαγραφή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BoatDetailsDialog
        boat={boats.find((b) => b.id === detailsId)}
        open={!!detailsId}
        onOpenChange={(o) => !o && setDetailsId(null)}
      />

      <BoatDocumentsDialog
        boatId={docsBoat?.id}
        boatName={docsBoat?.name}
        open={!!docsBoat}
        onOpenChange={(o) => !o && setDocsBoat(null)}
      />

      <Dialog open={!!fiscal} onOpenChange={(o) => !o && setFiscal(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle className="font-head">Έκδοση τιμολογίου / απόδειξης</DialogTitle></DialogHeader>
          {fiscal && (
            <div className="space-y-4">
              <div className="rounded-sm bg-secondary/50 p-3 text-sm">
                <div className="font-semibold">{fiscal.boat.name}</div>
                <div className="text-xs text-muted-foreground">{fiscal.boat.owner} · θέση {fiscal.boat.slot_label || `#${fiscal.boat.slot_number}`}</div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Τύπος παραστατικού</Label>
                <Select value={fiscal.doc_type} onValueChange={(v) => setFiscal({ ...fiscal, doc_type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="fiscal-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Απόδειξη</SelectItem>
                    <SelectItem value="invoice">Τιμολόγιο</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Συνολικό ποσό με ΦΠΑ (€)</Label>
                  <Input type="number" step="0.01" value={fiscal.amount} onChange={(e) => setFiscal({ ...fiscal, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="fiscal-amount-input" />
                  <div className="mt-1 text-[11px] text-muted-foreground" data-testid="fiscal-vat-hint">
                    Καθαρή αξία {(Number(fiscal.amount || 0) / 1.24).toFixed(2)} € · ΦΠΑ 24% {(Number(fiscal.amount || 0) - Number(fiscal.amount || 0) / 1.24).toFixed(2)} €
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Ημερομηνία</Label>
                  <DateField value={fiscal.doc_date} onChange={(v) => setFiscal({ ...fiscal, doc_date: v })} testId="fiscal-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Αιτιολογία</Label>
                <Input value={fiscal.note} onChange={(e) => setFiscal({ ...fiscal, note: e.target.value })} placeholder="προαιρετικό" className="mt-1 rounded-sm" data-testid="fiscal-note-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setFiscal(null)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={issueDoc} data-testid="fiscal-issue-btn">Έκδοση PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <button
        onClick={openNew}
        data-testid="boats-fab"
        aria-label="Νέο σκάφος"
        className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#0055FF] text-white shadow-lg transition-transform active:scale-95 md:hidden"
      >
        <Plus size={26} weight="bold" />
      </button>
    </div>
  );
}
